import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  Grid,
  CircularProgress,
  Typography,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  IconButton,
} from "@mui/material";
import toast from "react-hot-toast";
import axios from "axios";
import CloseIcon from "@mui/icons-material/Close";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import CancelIcon from "@mui/icons-material/Close";
import { axiosConfig } from "../../axios/axiosConfig";

const InfluencerCommissionModal = ({ open, onClose, influencerUserId }) => {
  const [plans, setPlans] = useState([]);
  const [selectedPlan, setSelectedPlan] = useState("");
  const [commissionAmount, setCommissionAmount] = useState("");
  const [loading, setLoading] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [commissionList, setCommissionList] = useState([]);
  const [commissionLoading, setCommissionLoading] = useState(false);
  const [editCommissionId, setEditCommissionId] = useState(null);

  const fetchPlans = async () => {
    setLoading(true);
    try {
      const res = await axiosConfig({
        method: "get",
        url: `/subscription/subscription_plan`,
      });
      const flatPlans = res.data.response.plans.flatMap((plan) => plan.data);
      setPlans(flatPlans);
    } catch (error) {
      toast.error("Failed to load plans");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      fetchPlans();
    }
  }, [open]);

  const fetchCommissionList = async () => {
    setCommissionLoading(true);
    try {
      const response = await axiosConfig({
        method: "get",
        url: `/tnibroadmin/influencer-commissions/`,
      });
      const data = response?.data?.response;
      setCommissionList(data);
    } catch (error) {
      const errMsg =
        error.response.data ||
        error.message ||
        "Request failed with status code 404";
      toast.error(errMsg);
    } finally {
      setCommissionLoading(false);
    }
  };
  useEffect(() => {
    fetchCommissionList();
  }, []);

  const handleSubmit = async () => {
    if (!selectedPlan || !commissionAmount) {
      toast.error("Please fill all fields");
      return;
    }

    setSubmitLoading(true);

    try {
      const payload = {
        influencer_user: influencerUserId,
        subscription_plan: selectedPlan,
        commission_amount: Number(commissionAmount),
      };

      if (editCommissionId) {
        // Update existing
        await axiosConfig({
          method: "put",
          url: `/tnibroadmin/influencer-commissions/${editCommissionId}/`,
          data: payload,
        });
        toast.success("Commission updated");
      } else {
        // Create new
        await axiosConfig({
          method: "post",
          url: `/tnibroadmin/influencer-commissions/`,
          data: payload,
        });
        toast.success("Commission added");
      }

      // Reset
      setSelectedPlan("");
      setCommissionAmount("");
      setEditCommissionId(null);
      fetchCommissionList();
    } catch(error) {
      const errMsg = error.response?.data?.non_field_errors[0] || error.message || "Request failed";
      console.log(error, "error")
      toast.error(errMsg);
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleEdit = (row) => {
    setEditCommissionId(row.id);
    setSelectedPlan(row.subscription_plan);
    setCommissionAmount(row.commission_amount);
  };

  const handleCancelEdit = () => {
    setEditCommissionId(null);
    setSelectedPlan("");
    setCommissionAmount("");
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <DialogTitle>Set Influencer Commission</DialogTitle>
        <DialogActions>
          <IconButton color="error" onClick={onClose} disabled={submitLoading}>
            <CloseIcon />
          </IconButton>
        </DialogActions>
      </div>
      <DialogContent
        dividers
        sx={{
          overflowX: "auto",
          "&::-webkit-scrollbar": {
            display: "none",
          },
          scrollbarWidth: "none", // For Firefox
        }}
      >
        <Grid container spacing={1} alignItems="center">
          <Grid item xs={5}>
            <FormControl fullWidth>
              <InputLabel>Select Plan</InputLabel>
              {loading ? (
                <CircularProgress size={24} />
              ) : (
                <Select
                  value={selectedPlan}
                  onChange={(e) => setSelectedPlan(e.target.value)}
                  label="Select Plan"
                >
                  {plans.map((plan) => (
                    <MenuItem key={plan.id} value={plan.id}>
                      {plan.name} - ₹{plan.price}
                    </MenuItem>
                  ))}
                </Select>
              )}
            </FormControl>
          </Grid>
          <Grid item xs={4}>
            <TextField
              fullWidth
              type="number"
              label="Commission Amount"
              value={commissionAmount}
              onChange={(e) => setCommissionAmount(e.target.value)}
            />
          </Grid>
          <Grid item xs={3}>
            <Button
              variant="contained"
              fullWidth
              onClick={handleSubmit}
              disabled={submitLoading}
              sx={{
                backgroundColor: "#339502",
                color: "#fff",
                padding: "14px 10px",
                "&:hover": {
                  backgroundColor: "#339502",
                  boxShadow: "0 4px 12px rgba(0, 0, 0, 0.3)",
                },
              }}
            >
              {submitLoading ? (
                <CircularProgress size={20} color="inherit" />
              ) : editCommissionId ? (
                "Update"
              ) : (
                "Submit"
              )}
            </Button>
            {editCommissionId && (
              <Button
                onClick={handleCancelEdit}
                color="error"
                fullWidth
                sx={{ mt: 1 }}
              >
                Cancel Edit
              </Button>
            )}
          </Grid>
        </Grid>
        <Typography
          variant="h5"
          sx={{
            mt: 4,
            mb: 2,
          }}
        >
          Influencer commission
        </Typography>
        {commissionLoading ? (
          <CircularProgress />
        ) : (
          <>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>ID</TableCell>
                    <TableCell>Commission Amount</TableCell>
                    <TableCell>Influencer Name</TableCell>
                    <TableCell>Plan Name</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {commissionList
                    .filter((row) => row.influencer_user === influencerUserId)
                    .map((row) => (
                      <TableRow key={row.id}>
                        <TableCell>{row.id}</TableCell>
                        <TableCell>{row.commission_amount}</TableCell>
                        <TableCell>{row.username}</TableCell>
                        <TableCell>{row.plan_name}</TableCell>
                        <TableCell>
                          <IconButton onClick={() => handleEdit(row)}>
                            <EditIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default InfluencerCommissionModal;
